import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiData, useAppDispatch } from 'reducers/hooks';
import { deleteSiteDesign, duplicateSiteDesign, getSiteDesigns, postSiteDesign, putSiteDesign } from "reducers/fell/siteDesign/siteDesignActions"
import { selectSiteDesigns, selectSiteDesignStatus } from "reducers/fell/siteDesign/siteDesignSlice"
import { selectSites, selectStatus } from 'reducers/fell/sites/sitesSlice'
import { SiteDesignType } from '../../lib/types';
import { ColumnDef } from '@tanstack/react-table';
import { EditableTextCell } from 'components/Table/EditableTextCell'
import { DeleteRowAction } from 'components/Table/DeleteRowAction'
import { NavigateAction } from 'components/Table/NavigateAction'
import { DuplicateRowAction } from 'components/Table/DuplicateRowAction'
import { getSitesThunk } from "reducers/fell/sites/action"
import { isNameLengthTooLong } from '../../lib/helpers';
import { getProjectsThunk } from '../../reducers/fell/projects/action';
import { Row } from "@tanstack/react-table";

type SiteDesignsData = {
    projectId: string,
    projectName: string,    
    siteId: string,
    siteName: string,    
    siteDesignsData,
    handleAddSiteDesign: () => void,
    handleDeleteSiteDesignRow: (rowId: number) => void,
    handleModalConfirmSiteDesignDeletion: () => void,
    handleModalCancelSiteDesignDeletion: () => void,
    handleDuplicateSiteDesignRow: (siteDesignId: number) => void,
    siteDesignsTableColumns: ColumnDef<SiteDesignType>[],
    showModal: boolean, 
    maxNameLength: number,
    hasNameError: boolean, 
    handleValidationMessageCloseEvent: () => void;
}

export const SiteDesignsInitialization = (): SiteDesignsData => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [siteDesignIdToDelete, setSiteDesignIdToDelete] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const { projectId, siteId } = useParams();
   
    const projectIdNumber = parseInt(projectId);
    const projectsData = useApiData(getProjectsThunk(), state => state.projects.data, state => state.projects.projectsStatus, navigate);
    const project = projectsData.find(project => project.id.toString() === projectId);
    const projectName = project ? project.name : "";

    const siteIdNumber = parseInt(siteId)    
    const [siteName, setSiteName] = useState("");
    const sitesData = useApiData(getSitesThunk(projectId), selectSites(projectId), selectStatus(projectId), navigate);
    useEffect(() => {
        const siteData = sitesData.find(site => site.id === siteIdNumber)
        if (siteData) {
            const currentSiteName = siteData ? siteData.name : "";
            setSiteName(currentSiteName);
        }
    }, [sitesData, siteIdNumber]);   

    const [siteDesignsData, setSiteDesignsData] = useState<SiteDesignType[]>([]);
    const [localUpdateQueue, setLocalUpdateQueue] = useState<any>({}); // Tracks the updates that haven't been saved yet
    const [isEditing, setIsEditing] = useState(false);
    const maxNameLength = 50;
    const [hasNameError, setHasNameError] = useState(false);
    const siteDesigns = useApiData(
        getSiteDesigns({ projectId: projectIdNumber, siteId: siteIdNumber }), selectSiteDesigns(projectIdNumber, siteIdNumber),
        selectSiteDesignStatus(projectIdNumber, siteIdNumber), navigate)

    useEffect(() => {
        if (!isEditing) {
            const updatedData = siteDesigns.map((siteDesign) => {
                const queuedUpdate = localUpdateQueue[siteDesign.id];
                if (queuedUpdate) {
                    return { ...siteDesign, ...queuedUpdate };
                }
                return siteDesign;
            });
            setSiteDesignsData(updatedData);
        }
    }, [siteDesigns, isEditing, localUpdateQueue]);   

    const handleAddSiteDesign = async () => {

        try {

            await dispatch(postSiteDesign({
                projectId: projectIdNumber,
                siteId: siteIdNumber,
                dispatch
            }));

            await dispatch(getSiteDesigns({
                projectId: projectIdNumber,
                siteId: siteIdNumber
            }))

        } catch (error) {
            console.error("Error adding site design:", error);
            navigate('/error-page');
        }
    }

    const handleDeleteSiteDesignRow = (siteDesignId: number) => {
        if (siteDesignId >= 0) {
            setSiteDesignIdToDelete(siteDesignId);
            setShowModal(true);
        }
    };

    const handleModalConfirmSiteDesignDeletion = async () => {

        if (siteDesignIdToDelete !== null) {
            const siteDesignToDelete = siteDesignsData.find(siteDesign => siteDesign.id === siteDesignIdToDelete);
            if (siteDesignToDelete) {
                const siteDesignId = siteDesignToDelete.id;

                try {
                    await dispatch(deleteSiteDesign({
                        projectId: projectIdNumber,
                        siteId: siteIdNumber,
                        designId: siteDesignId
                    }));

                    await dispatch(getSiteDesigns({
                        projectId: projectIdNumber,
                        siteId: siteIdNumber
                    }));

                } catch (error) {
                    console.error("Error deleting site design:", error);
                    navigate('/error-page');
                }
            }
        }

        setShowModal(false);
    };

    const handleModalCancelSiteDesignDeletion = () => {
        setShowModal(false);
    };

    const handleDuplicateSiteDesignRow = async (siteDesignId: number) => {
        if (siteDesignId >= 0) {
            const siteDesignToDuplicate = siteDesignsData.find(siteDesign => siteDesign.id === siteDesignId);
            if (siteDesignToDuplicate) {
                const checkNameTooLong = isNameLengthTooLong(siteDesignToDuplicate.name, maxNameLength - 8)
                setHasNameError(checkNameTooLong)  // "Copy of " = 8 characters for duplicate site design name
                if (!checkNameTooLong) {
                    try {
                        await dispatch(duplicateSiteDesign({
                            projectId: projectIdNumber,
                            siteId: siteIdNumber,
                            siteDesignId: siteDesignToDuplicate.id
                        }));

                        await dispatch(getSiteDesigns({
                            projectId: projectIdNumber,
                            siteId: siteIdNumber
                        }));

                    } catch (error) {
                        console.error("Error duplicating site design:", error);
                        navigate('/error-page');
                    }
                }
            }
        }
    }

    const handleUpdateSiteDesign = async (row: Row<any>, columnId: string, value: string) => {

        if (row) {
            const updatedSiteDesignDataIndex = siteDesignsData.findIndex(site => site.id === row.original.id);

            if (updatedSiteDesignDataIndex >= 0) {
                const updatedSiteDesignData = [...siteDesignsData];
                updatedSiteDesignData[updatedSiteDesignDataIndex] = { ...updatedSiteDesignData[updatedSiteDesignDataIndex], [columnId]: value };

                if (updatedSiteDesignData) {
                    // to be sure that the name of the site design after updating is still inferior than 50 characters
                    const currentSiteDesign = siteDesignsData[updatedSiteDesignDataIndex];
                    const updatedSiteDesign = updatedSiteDesignData[updatedSiteDesignDataIndex];
                    if (currentSiteDesign && updatedSiteDesign) {
                        if (currentSiteDesign.name !== updatedSiteDesign.name) { //checks if the name has been changed otherwise we don't post anything
                            const checkNameTooLong = isNameLengthTooLong(updatedSiteDesign.name, maxNameLength);
                            setHasNameError(checkNameTooLong)

                            if (!checkNameTooLong) {
                                setIsEditing(true);
                                setSiteDesignsData(updatedSiteDesignData);                                

                                // Track changes in the local update queue to avoid flicker on re-render
                                const siteDesignId = updatedSiteDesign.id;
                                setLocalUpdateQueue((prevQueue) => ({
                                    ...prevQueue,
                                    [siteDesignId]: { [columnId]: value }
                                }));

                                try {
                                    await dispatch(putSiteDesign({
                                        projectId: projectIdNumber,
                                        siteId: siteIdNumber,
                                        siteDesign: updatedSiteDesign,
                                        dispatch
                                    }));

                                    await dispatch(getSiteDesigns({
                                        projectId: projectIdNumber,
                                        siteId: siteIdNumber
                                    }));

                                } catch (error) {
                                    console.error("Error updating site design:", error);
                                    navigate('/error-page');
                                } finally {
                                    setIsEditing(false); // Reset editing state            
                                }
                            }
                        }
                    }
                }
            }
        }
    };

    const handleValidationMessageCloseEvent = () => {
        setHasNameError(false)
    };

    const siteDesignsTableColumns = [
        {
            accessorKey: 'primaryActions',
            header: '',
            cell: ({ row }) => (
                <div className="row-action-container">
                    <DuplicateRowAction row={row} duplicateClickHandler={handleDuplicateSiteDesignRow} />
                    <NavigateAction rowIndex={row.index} url={`/project/${projectId}/site/${siteId}/sitedesign/${row.original.id}`} />
                </div>
            )
        },
        {
            accessorKey: 'name',
            header: 'Site Design',
            cell: ({ row, column }) => (
                <EditableTextCell
                    inputId={"xsd__e2e-site-designs-table-row-" + row.id + "-cell-input-" + column.id}
                    value={row?.original?.name}
                    row={row}
                    column={column}
                    updateCellData={handleUpdateSiteDesign}
                    maxLength={maxNameLength}
                />
            ),
        },
        {
            accessorKey: "formattedCreatedUtc",
            header: "Created"
        },
        {
            accessorKey: "formattedUpdatedUtc",
            header: "Modified"
        },
        {
            accessorKey: 'secondaryActions',
            header: '',
            cell: ({ row }) => (
                <div className="row-action-container">
                    <DeleteRowAction row={row} deleteClickHandler={handleDeleteSiteDesignRow} />
                </div>
            )
        }
    ]   


    return {
        projectId,
        projectName,
        siteId,
        siteName,        
        siteDesignsData,
        handleAddSiteDesign,
        handleDeleteSiteDesignRow,
        handleModalConfirmSiteDesignDeletion,
        handleModalCancelSiteDesignDeletion,
        handleDuplicateSiteDesignRow,
        siteDesignsTableColumns,
        showModal,
        maxNameLength,
        hasNameError, 
        handleValidationMessageCloseEvent
    }
}
